import { Fragment, type ReactNode } from 'react';

import {
    parseWhatsAppFormatting,
    WHATSAPP_FORMAT_STYLES,
    type WhatsAppFormattedNode,
} from '~/features/whatsapp/utils/parseWhatsAppFormatting';

type RenderText = (value: string) => ReactNode;

type Props = {
    text: string;
    // Lets callers swap plain text runs for richer nodes (e.g. template placeholder chips).
    renderText?: RenderText;
};

const toReactNodes = (nodes: WhatsAppFormattedNode[], renderText?: RenderText): ReactNode[] =>
    nodes.map((node, index) => {
        // Re-derived every render, no identity of its own; positional keys are stable for a given string.
        const key = index;

        if (node.type === 'text')
            return renderText ? <Fragment key={key}>{renderText(node.value)}</Fragment> : node.value;

        if (node.type === 'quote') {
            return (
                <blockquote key={key} className="my-0 border-l-2 border-grey pl-2">
                    {toReactNodes(node.children, renderText)}
                </blockquote>
            );
        }

        if (node.type === 'bulletList') {
            return (
                <ul key={key} className="my-0 list-disc pl-4">
                    {node.items.map((item, itemIndex) => (
                        <li key={itemIndex}>{toReactNodes(item, renderText)}</li>
                    ))}
                </ul>
            );
        }

        if (node.type === 'numberedList') {
            return (
                <ol key={key} className="my-0 list-decimal pl-4">
                    {node.items.map((item, itemIndex) => {
                        // Number() rounds a 20-digit written number, so drop the attribute rather than renumber it.
                        const number = Number(item.number);
                        return (
                            <li key={itemIndex} value={Number.isSafeInteger(number) ? number : undefined}>
                                {toReactNodes(item.children, renderText)}
                            </li>
                        );
                    })}
                </ol>
            );
        }

        const inner = toReactNodes(node.children, renderText);
        switch (node.style) {
            case WHATSAPP_FORMAT_STYLES.BOLD:
                return <strong key={key}>{inner}</strong>;
            case WHATSAPP_FORMAT_STYLES.ITALIC:
                return <em key={key}>{inner}</em>;
            case WHATSAPP_FORMAT_STYLES.STRIKETHROUGH:
                return <s key={key}>{inner}</s>;
            case WHATSAPP_FORMAT_STYLES.MONOSPACE:
                return (
                    <code key={key} className="font-mono text-[0.9em]">
                        {inner}
                    </code>
                );
            case WHATSAPP_FORMAT_STYLES.INLINE_CODE:
                return (
                    <code key={key} className="rounded bg-surface-light px-0.5 font-mono text-[0.9em]">
                        {inner}
                    </code>
                );
            default:
                return <span key={key}>{inner}</span>;
        }
    });

export const WhatsAppFormattedText = ({ renderText, text }: Props) => (
    <>{toReactNodes(parseWhatsAppFormatting(text), renderText)}</>
);
